import { useQuery, useQueries } from '@tanstack/react-query';
import {
	useSettings,
	useUnits,
	getCurrency,
	convertToImperialNoIntlFormat,
} from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations';
import {
	RehabAction,
	CostUnit,
	isCostTableAction,
	CostApplication,
} from '@Types';
import type { CostTableDataEntry, CostTableSummary } from '@Types/cost.types';
import {
	costTablesListQuery,
	costTableDataQuery,
} from '../../queries/costTableQueries';

export interface ActionTableRow {
	actionId: string;
	costSource: string;
	costTableName: string;
	unitCostDisplay: string;
	costPerDisplay: string;
	costApplication: string;
	notes: string;
}

function getUnitCostDisplay(
	entries: CostTableDataEntry[] | undefined,
	toCurrency: (value: number) => string,
): string {
	if (!entries) return '-';
	const costs = entries
		.map(e => e.unit_cost)
		.filter((c): c is number => c !== null);
	if (costs.length === 0) return '-';
	const min = Math.min(...costs);
	const max = Math.max(...costs);
	return min === max
		? toCurrency(min)
		: `${toCurrency(min)} - ${toCurrency(max)}`;
}

function getCostApplicationDisplay(
	costApplication: CostApplication | undefined,
	system: string,
	t: (key: string) => string,
): string {
	const isImperial = system === 'Imperial';
	if (costApplication === CostApplication.trenchLength) {
		return isImperial ? t('/ft (rehab length)') : t('/m (rehab length)');
	}
	return isImperial ? t('/ft (pipe length)') : t('/m (pipe length)');
}

function getCustomCostPerDisplay(
	unit: CostUnit | undefined,
	unitCost: number,
	costApplication: CostApplication | undefined,
	system: string,
	t: (key: string) => string,
): { display: string; convertedCost: number } {
	if (unit === CostUnit.length) {
		const convertedCost =
			system === 'Imperial'
				? +convertToImperialNoIntlFormat(`${unitCost} cost/m`).value
				: unitCost;
		// Custom-cost actions present a single merged "Cost per" control
		// (each / pipe length / rehab length) in the drawer, so this column
		// mirrors that here rather than splitting unit and application.
		return {
			display: getCostApplicationDisplay(costApplication, system, t),
			convertedCost,
		};
	}
	return { display: unit?.toString() ?? 'each', convertedCost: unitCost };
}

export function useActionTableRows(
	rehabActions: RehabAction[],
): ActionTableRow[] {
	const { t } = useGlobalization();
	const { companySettings } = useSettings();
	const { system } = useUnits();

	const hasCostTableActions = rehabActions.some(isCostTableAction);
	const { data: costTableList = [] } = useQuery<CostTableSummary[]>(
		costTablesListQuery(hasCostTableActions),
	);

	const costTableMap = new Map<string, CostTableSummary>(
		costTableList.map(ct => [ct.id, ct]),
	);

	// Deduplicate cost table data queries by costTableId:costTableDataId
	// (mirrors the deduplication pattern in CostTableStalenessWarning)
	const seen = new Set<string>();
	const uniqueDataQueries: {
		costTableId: string;
		costTableDataId: string;
	}[] = [];
	for (const action of rehabActions) {
		if (isCostTableAction(action)) {
			const key = `${action.costTableId}:${action.costTableDataId}`;
			if (!seen.has(key)) {
				seen.add(key);
				uniqueDataQueries.push({
					costTableId: action.costTableId,
					costTableDataId: action.costTableDataId,
				});
			}
		}
	}

	const dataQueryResults = useQueries({
		queries: uniqueDataQueries.map(({ costTableId, costTableDataId }) =>
			costTableDataQuery(costTableId, costTableDataId),
		),
	});

	const dataEntriesMap = new Map<string, CostTableDataEntry[]>();
	uniqueDataQueries.forEach(({ costTableId, costTableDataId }, i) => {
		const entries = dataQueryResults[i]?.data;
		if (entries) {
			dataEntriesMap.set(`${costTableId}:${costTableDataId}`, entries);
		}
	});

	const toCurrency = (value: number): string =>
		getCurrency(value, companySettings) ?? '-';

	return rehabActions.filter(Boolean).map((action): ActionTableRow => {
		if (isCostTableAction(action)) {
			const tableInfo = costTableMap.get(action.costTableId);
			const entries = dataEntriesMap.get(
				`${action.costTableId}:${action.costTableDataId}`,
			);
			return {
				actionId: action.actionId,
				costSource: t('Cost table'),
				costTableName: tableInfo?.name ?? '-',
				unitCostDisplay: getUnitCostDisplay(entries, toCurrency),
				costPerDisplay: tableInfo?.cost_per ?? '-',
				costApplication: getCostApplicationDisplay(
					action.costApplication,
					system,
					t,
				),
				notes: action.notes,
			};
		}

		const { display: costPerDisplay, convertedCost } =
			getCustomCostPerDisplay(
				action.unit,
				action.unitCost,
				action.costApplication,
				system,
				t,
			);
		return {
			actionId: action.actionId,
			costSource: t('Custom cost'),
			costTableName: '-',
			unitCostDisplay: toCurrency(convertedCost),
			costPerDisplay,
			costApplication: '-',
			notes: action.notes,
		};
	});
}
